import React, { useEffect, useState } from "react";
import { apiGet, apiPost, apiDelete } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "../components/ui/select";
import { Loader2, Sparkles, Trash2, Plus } from "lucide-react";

const empty = {
  stem: "",
  options: ["", "", "", ""],
  correct_index: 0,
  explanation: "",
  specialty: "Medicina Interna",
  topic: "",
  difficulty: "medium",
  is_premium: false,
};

export default function Admin() {
  const [questions, setQuestions] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [form, setForm] = useState(empty);
  const [aiForm, setAiForm] = useState({ specialty: "Medicina Interna", topic: "", difficulty: "medium", count: 3 });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const docs = await apiGet("/admin/questions", { limit: 200 });
    setQuestions(docs);
  };
  useEffect(() => {
    load();
    apiGet("/specialties").then((r) => setSpecialties(r.specialties || []));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiPost("/admin/questions", {
        ...form,
        correct_index: Number(form.correct_index),
      });
      setMsg("Domanda creata.");
      setForm(empty);
      load();
    } catch (e) {
      setMsg("Errore: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (qid) => {
    if (!window.confirm("Eliminare la domanda?")) return;
    await apiDelete(`/admin/questions/${qid}`);
    load();
  };

  const generate = async () => {
    setGenerating(true);
    setMsg("");
    try {
      const r = await apiPost("/admin/ai/generate", aiForm);
      setMsg(`Create ${r.count} domande con IA.`);
      load();
    } catch (e) {
      setMsg("Errore IA: " + (e?.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="px-6 sm:px-10 py-8 lg:py-12 max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="eyebrow text-brand-gold">// Pannello admin</div>
        <h1 className="heading-display text-4xl sm:text-5xl text-brand-primary mt-1">Gestione domande</h1>
      </div>

      {msg && (
        <div className="mb-4 border border-brand-green/40 bg-brand-green/8 rounded-xl px-4 py-3 text-sm text-brand-primary" data-testid="admin-msg">
          {msg}
        </div>
      )}

      {/* AI generation */}
      <div className="border border-brand-gold/40 bg-brand-gold/5 rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-4 text-brand-gold">
          <Sparkles className="h-4 w-4" />
          <span className="eyebrow text-brand-gold">// Genera con AI (GPT-5.2)</span>
        </div>
        <div className="grid sm:grid-cols-4 gap-3">
          <Select value={aiForm.specialty} onValueChange={(v) => setAiForm({ ...aiForm, specialty: v })}>
            <SelectTrigger data-testid="ai-specialty" className="bg-brand-background border-brand-border rounded-xl h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              {specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="ai-topic" placeholder="Argomento (opzionale)" value={aiForm.topic} onChange={(e) => setAiForm({ ...aiForm, topic: e.target.value })} className="bg-brand-background border-brand-border rounded-xl h-11" />
          <Select value={aiForm.difficulty} onValueChange={(v) => setAiForm({ ...aiForm, difficulty: v })}>
            <SelectTrigger data-testid="ai-difficulty" className="bg-brand-background border-brand-border rounded-xl h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="easy">Facile</SelectItem>
              <SelectItem value="medium">Media</SelectItem>
              <SelectItem value="hard">Difficile</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Input data-testid="ai-count" type="number" min={1} max={5} value={aiForm.count} onChange={(e) => setAiForm({ ...aiForm, count: Number(e.target.value) })} className="bg-brand-background border-brand-border rounded-xl h-11 w-20" />
            <Button data-testid="ai-generate-btn" onClick={generate} disabled={generating} className="flex-1 rounded-xl bg-brand-gold hover:brightness-95 text-brand-primary h-11">
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Genera <Sparkles className="h-4 w-4 ml-1" /></>}
            </Button>
          </div>
        </div>
      </div>

      {/* Manual creation */}
      <form onSubmit={submit} className="premium-card p-6 mb-6 space-y-4">
        <div className="flex items-center gap-2 text-brand-secondary mb-2">
          <Plus className="h-4 w-4" />
          <span className="eyebrow">// Crea domanda manualmente</span>
        </div>
        <div>
          <label className="eyebrow mb-2 block">Caso clinico</label>
          <Textarea data-testid="admin-stem" required value={form.stem} onChange={(e) => setForm({ ...form, stem: e.target.value })} className="bg-brand-background border-brand-border rounded-xl" rows={3} />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {form.options.map((o, i) => (
            <Input
              key={`opt-${String.fromCharCode(65 + i)}`}
              data-testid={`admin-option-${i}`}
              required
              placeholder={`Opzione ${String.fromCharCode(65 + i)}`}
              value={o}
              onChange={(e) => {
                const opts = [...form.options];
                opts[i] = e.target.value;
                setForm({ ...form, options: opts });
              }}
              className="bg-brand-background border-brand-border rounded-xl h-11"
            />
          ))}
        </div>
        <div className="grid sm:grid-cols-4 gap-3">
          <Select value={String(form.correct_index)} onValueChange={(v) => setForm({ ...form, correct_index: Number(v) })}>
            <SelectTrigger data-testid="admin-correct" className="bg-brand-background border-brand-border rounded-xl h-11"><SelectValue placeholder="Risposta corretta" /></SelectTrigger>
            <SelectContent>
              {form.options.map((_, i) => <SelectItem key={`correct-${String.fromCharCode(65 + i)}`} value={String(i)}>{String.fromCharCode(65 + i)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={form.specialty} onValueChange={(v) => setForm({ ...form, specialty: v })}>
            <SelectTrigger data-testid="admin-specialty" className="bg-brand-background border-brand-border rounded-xl h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              {specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="admin-topic" required placeholder="Argomento" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="bg-brand-background border-brand-border rounded-xl h-11" />
          <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
            <SelectTrigger data-testid="admin-difficulty" className="bg-brand-background border-brand-border rounded-xl h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="easy">Facile</SelectItem>
              <SelectItem value="medium">Media</SelectItem>
              <SelectItem value="hard">Difficile</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="eyebrow mb-2 block">Spiegazione</label>
          <Textarea data-testid="admin-explanation" required value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} className="bg-brand-background border-brand-border rounded-xl" rows={3} />
        </div>
        <Button data-testid="admin-save-btn" type="submit" disabled={saving} className="rounded-xl bg-brand-green hover:brightness-110 text-white h-11 px-6">
          {saving ? "Salvataggio..." : "Salva domanda"}
        </Button>
      </form>

      {/* List */}
      <div className="premium-card overflow-hidden">
        <div className="px-5 py-3 border-b border-brand-border flex justify-between items-center bg-brand-surfaceElevated">
          <div className="eyebrow">// Tutte le domande ({questions.length})</div>
        </div>
        <div className="divide-y divide-brand-borderSubtle">
          {questions.map((q) => (
            <div key={q.question_id} className="px-5 py-3 flex justify-between items-start gap-3 hover:bg-brand-surfaceElevated/50">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-brand-primary line-clamp-2">{q.stem}</div>
                <div className="mt-1.5 eyebrow flex gap-3 flex-wrap text-[10px]">
                  <span className="text-brand-green">{q.specialty}</span>
                  <span>{q.topic}</span>
                  <span>{q.difficulty}</span>
                  {q.is_premium && <span className="text-brand-gold">Premium</span>}
                  {q.ai_generated && <span className="text-brand-gold">AI</span>}
                </div>
              </div>
              <button
                data-testid={`admin-delete-${q.question_id}`}
                onClick={() => remove(q.question_id)}
                className="btn-tactile p-2 rounded-lg text-brand-secondary hover:text-brand-red hover:bg-brand-red/8 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
